import { useSearchParams } from 'react-router-dom';
import {
    Badge, Button, Card, EmptyState, LoadingList, SectionHeader, cx, useIsPhone,
} from '@timely/ui';
import {
    EMPTY_STOCK_FILTERS, holds, matchesStockFilters, stockAvailability, summariseStock,
    type StockAvailability, type StockFilters, type StockItem,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { useStock } from '../stock/useStock';
import { StockConnect } from '../stock/StockConnect';

/**
 * Live stock.
 *
 * A live read of a company's own stock sheet, exactly as the reference app
 * did it and for the same reason: a CRM on a call needs to know what is on
 * the shelf as much as a manager does, so `stock.view` is on every default
 * role including Collector and Viewer. What changes per role is **price**,
 * and only price — `stock.view.prices` decides whether `rate`/`value` were
 * ever in the response this screen received, redacted server-side before it
 * arrived, never hidden by a check in this file.
 *
 * Search, category and availability all live in this screen's own URL, the
 * same pattern the cheque register uses — a term typed here cannot bleed into
 * the book's search or be emptied by leaving the tab.
 */
export function Stock() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { money, count } = useFormatters();
    const isPhone = useIsPhone();

    const canManage = holds(context, 'datasource.manage');
    const [params, setParams] = useSearchParams();
    const filters = filtersFromParams(params);

    const { configured, items, priced, truncated, fetchedAt, statusDetail, loading, error, refresh } =
        useStock(workspace.id);

    const set = (changes: Partial<StockFilters>) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            if (value === '' || value === 'all') next.delete(key); else next.set(key, String(value));
        }
        setParams(next, { replace: true });
    };

    const summary = summariseStock(items);
    const visible = items.filter(item => matchesStockFilters(item, filters));

    return (
        <Page>
            <SectionHeader
                title="Live stock"
                subtitle="Read from your stock sheet. Nothing here is imported or stored."
                actions={
                    configured ? (
                        <Button variant="secondary" disabled={loading} onClick={() => { void refresh(); }}>
                            {loading ? 'Reading…' : 'Refresh'}
                        </Button>
                    ) : undefined
                }
            />

            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}
            {truncated && (
                <div className="mt-4">
                    <FormNotice tone="error">
                        This sheet has more items than one read can show. Showing the first ones only.
                    </FormNotice>
                </div>
            )}

            {!loading && configured && fetchedAt && (
                <p className="mt-3 text-[12.5px] text-label-3">
                    Read {agoText(fetchedAt)}{!priced && ' · prices are not shown for your role'}
                </p>
            )}

            {loading ? (
                <Card className="mt-5 overflow-hidden"><LoadingList label="Reading your stock sheet" rows={6} /></Card>
            ) : !configured ? (
                <div className="mt-5">
                    <StockConnect workspaceId={workspace.id} canManage={canManage} statusDetail={statusDetail} onConnected={() => { void refresh(); }} />
                </div>
            ) : (
                <>
                    <SummaryTiles
                        summary={summary}
                        money={priced ? money : null}
                        active={filters.availability}
                        onPick={a => set({ availability: a === filters.availability ? 'all' : a })}
                    />

                    <FilterBar
                        filters={filters}
                        categories={summary.categories}
                        onChange={set}
                        onClear={() => setParams(new URLSearchParams(), { replace: true })}
                    />

                    <Card className="mt-5 overflow-hidden">
                        {items.length === 0 ? (
                            <EmptyState
                                title="Nothing on the sheet"
                                hint="Your stock sheet was read successfully, but it has no items in it yet."
                            />
                        ) : visible.length === 0 ? (
                            <EmptyState
                                title="Nothing matches"
                                hint={`None of your ${count(items.length)} items match what you are looking for.`}
                                action={
                                    <Button variant="secondary" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                                        Clear filters
                                    </Button>
                                }
                            />
                        ) : isPhone ? (
                            <ul className="divide-y divide-separator">
                                {visible.map(item => <StockCard key={item.id} item={item} money={priced ? money : null} />)}
                            </ul>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-[13px]">
                                    <thead>
                                        <tr className="text-left text-label-3 text-[12px]">
                                            <th scope="col" className="font-semibold px-5 py-2.5">Item</th>
                                            <th scope="col" className="font-semibold px-3 py-2.5">Category</th>
                                            <th scope="col" className="font-semibold px-3 py-2.5 text-right">Quantity</th>
                                            <th scope="col" className="font-semibold px-3 py-2.5">Availability</th>
                                            {priced && <th scope="col" className="font-semibold px-3 py-2.5 text-right">Rate</th>}
                                            {priced && <th scope="col" className="font-semibold px-5 py-2.5 text-right">Value</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-separator">
                                        {visible.map(item => (
                                            <StockRow key={item.id} item={item} money={priced ? money : null} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>

                    {visible.length < items.length && (
                        <p className="mt-3 text-[12.5px] text-label-3">
                            Showing {count(visible.length)} of {count(items.length)}
                        </p>
                    )}
                </>
            )}
        </Page>
    );
}

/* --------------------------------- tiles ---------------------------------- */

const AVAILABILITY_LABEL: Record<StockAvailability, string> = { in: 'In stock', low: 'Low', out: 'Out of stock' };
const AVAILABILITY_TONE: Record<StockAvailability, 'pos' | 'warn' | 'dang'> = { in: 'pos', low: 'warn', out: 'dang' };

function SummaryTiles({
    summary, money, active, onPick,
}: {
    summary: ReturnType<typeof summariseStock>;
    money: ReturnType<typeof useFormatters>['money'] | null;
    active: StockFilters['availability'];
    onPick: (a: StockAvailability) => void;
}) {
    const tiles: { key: StockAvailability | 'total'; label: string; value: string }[] = [
        { key: 'total', label: 'Total items', value: String(summary.total) },
        { key: 'in', label: 'In stock', value: String(summary.inStock) },
        { key: 'low', label: 'Low', value: String(summary.low) },
        { key: 'out', label: 'Out of stock', value: String(summary.out) },
    ];

    return (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 max-md:flex max-md:overflow-x-auto max-md:snap-x max-md:snap-mandatory">
            {tiles.map(tile => (
                <button
                    key={tile.key}
                    type="button"
                    disabled={tile.key === 'total'}
                    onClick={() => tile.key !== 'total' && onPick(tile.key)}
                    className={cx(
                        'text-left rounded-[14px] px-4 py-3 shadow-e1 shrink-0 max-md:w-[42%] max-md:snap-start',
                        active === tile.key ? 'bg-accent-tint' : 'bg-card',
                        tile.key !== 'total' && 'cursor-pointer',
                    )}
                >
                    <p className="text-[12px] text-label-3 font-semibold">{tile.label}</p>
                    <p className="text-[20px] font-extrabold text-label num tabular-nums mt-0.5">{tile.value}</p>
                </button>
            ))}
            {money && summary.totalValue != null && (
                <div className="col-span-2 md:col-span-4 mt-1 text-[13px] text-label-3">
                    Total value: <strong className="text-label num tabular-nums">{money.compact(summary.totalValue)}</strong>
                </div>
            )}
        </div>
    );
}

/* -------------------------------- filters ---------------------------------- */

function FilterBar({
    filters, categories, onChange, onClear,
}: {
    filters: StockFilters;
    categories: string[];
    onChange: (changes: Partial<StockFilters>) => void;
    onClear: () => void;
}) {
    const narrowed = (filters.search ? 1 : 0) + (filters.category !== 'all' ? 1 : 0) + (filters.availability !== 'all' ? 1 : 0);

    return (
        <div className="mt-4 flex flex-wrap items-center gap-2">
            <input
                type="search"
                aria-label="Search stock"
                value={filters.search}
                onChange={e => onChange({ search: e.target.value })}
                placeholder="Search stock by item, code or category"
                className="h-11 max-md:h-11 flex-1 min-w-[200px] rounded-full bg-card px-4 text-[14px] shadow-e1 outline-none focus:ring-2 focus:ring-accent"
            />
            {categories.length > 0 && (
                <select
                    aria-label="Category"
                    value={filters.category}
                    onChange={e => onChange({ category: e.target.value })}
                    className="h-11 rounded-full bg-card px-4 text-[13.5px] shadow-e1 outline-none"
                >
                    <option value="all">Every category</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            )}
            {narrowed > 0 && (
                <Button variant="secondary" size="sm" onClick={onClear}>Reset</Button>
            )}
        </div>
    );
}

/* ---------------------------------- rows ----------------------------------- */

function StockRow({ item, money }: { item: StockItem; money: ReturnType<typeof useFormatters>['money'] | null }) {
    const availability = stockAvailability(item);
    return (
        <tr>
            <td className="px-5 py-3">
                <p className="font-semibold text-label">{item.name}</p>
                {item.code && <p className="text-[12px] text-label-3">{item.code}</p>}
            </td>
            <td className="px-3 py-3 text-label-2">{item.category ?? '—'}</td>
            <td className="px-3 py-3 text-right num tabular-nums">
                {item.quantity ?? '—'}{item.unit && item.quantity != null && <span className="text-label-3"> {item.unit}</span>}
            </td>
            <td className="px-3 py-3">
                <Badge tone={AVAILABILITY_TONE[availability]}>{AVAILABILITY_LABEL[availability]}</Badge>
            </td>
            {money && <td className="px-3 py-3 text-right num tabular-nums">{item.rate != null ? money.compact(item.rate) : '—'}</td>}
            {money && <td className="px-5 py-3 text-right num tabular-nums">{item.value != null ? money.compact(item.value) : '—'}</td>}
        </tr>
    );
}

function StockCard({ item, money }: { item: StockItem; money: ReturnType<typeof useFormatters>['money'] | null }) {
    const availability = stockAvailability(item);
    return (
        <li className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="font-semibold text-label truncate">{item.name}</p>
                    <p className="text-[12px] text-label-3">
                        {[item.code, item.category].filter(Boolean).join(' · ') || '—'}
                    </p>
                </div>
                <Badge tone={AVAILABILITY_TONE[availability]}>{AVAILABILITY_LABEL[availability]}</Badge>
            </div>
            <div className="mt-2 flex items-baseline justify-between text-[13px]">
                <span className="num tabular-nums">
                    {item.quantity ?? '—'}{item.unit && item.quantity != null && ` ${item.unit}`}
                </span>
                {money && item.value != null && (
                    <span className="num tabular-nums font-semibold">{money.compact(item.value)}</span>
                )}
            </div>
        </li>
    );
}

/* -------------------------------- helpers ----------------------------------- */

const agoText = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return new Date(iso).toLocaleDateString();
};

export function filtersFromParams(params: URLSearchParams): StockFilters {
    const availability = params.get('availability');
    return {
        search: params.get('search') ?? EMPTY_STOCK_FILTERS.search,
        category: params.get('category') ?? EMPTY_STOCK_FILTERS.category,
        availability: availability === 'in' || availability === 'low' || availability === 'out' ? availability : 'all',
    };
}
